import { useTranslation } from "react-i18next";
import React, { useState, useEffect } from "react";
import {
  getAllOrganizations,
  getUsersForManagement,
} from "../firestoreService";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
} from "@mui/material";

function OrgUserReport() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [usersByOrg, setUsersByOrg] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const [orgsData, usersDataRaw] = await Promise.all([
          getAllOrganizations(),
          getUsersForManagement(),
        ]);

        setOrganizations(Array.isArray(orgsData) ? orgsData : []);

        const usersArray = usersDataRaw?.users || [];

        const grouped = usersArray.reduce((acc, u) => {
          const orgId = u.organizationId || "unknown";
          (acc[orgId] = acc[orgId] || []).push(u);
          return acc;
        }, {});
        setUsersByOrg(grouped);
      } catch (e) {
        console.error("Failed to fetch organization report data:", e);
        setError(t("orgUserReport.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("orgUserReport.title")}
      </Typography>

      {organizations.map((org) => {
        const userList = usersByOrg[org.id] || [];
        return (
          <Paper key={org.id} sx={{ my: 3, p: 2 }} variant="outlined">
            <Typography variant="h5" component="h2" gutterBottom>
              {org.name || t("orgUserReport.unnamedOrg")}{" "}
              <Typography
                component="code"
                sx={{ fontSize: "0.8em", color: "text.secondary" }}
              >
                ({org.id})
              </Typography>
            </Typography>

            {userList.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("orgUserReport.displayName")}</TableCell>
                      <TableCell>{t("common.email")}</TableCell>
                      <TableCell>{t("orgUserReport.uid")}</TableCell>{" "}
                      {/* NEW COLUMN */}
                      <TableCell>{t("common.role")}</TableCell>
                      <TableCell>{t("common.status")}</TableCell>
                      <TableCell align="right">{t("common.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userList.map((user) => (
                      <TableRow key={user.uid}>
                        <TableCell>{user.displayName || "—"}</TableCell>
                        <TableCell>{user.email || "—"}</TableCell>
                        {/* NEW CELL with UID */}
                        <TableCell>
                          <Typography
                            component="code"
                            sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                          >
                            {user.uid}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.role || "—"}</TableCell>
                        <TableCell>{user.status || "—"}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => navigate(`/app/user-management`)} // Placeholder action
                          >
                            {t("orgUserReport.manage")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t("orgUserReport.noUsers")}
              </Typography>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

export default OrgUserReport;
